import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { BackButton } from 'components/forms/atoms/FormButtons';
import { type FC, useEffect, useRef } from 'react';
import { GLASS_BACKGROUND, GLASS_BLUR, NAVY, RADIUS_CARD, RADIUS_ITEM, SHADOW, SURFACE_TINT } from 'styles/tokens';

interface PromptSectionOption {
  name: string;
  displayName: string;
}

interface PromptSectionPickerDialogProps {
  open: boolean;
  promptSections: PromptSectionOption[];
  onClose: () => void;
  onInsert: (tag: string) => void;
}

/**
 * A direct, searchable path for inserting a prompt section. Selecting an option
 * immediately inserts its tag so repeated section additions stay fast.
 */
const PromptSectionPickerDialog: FC<PromptSectionPickerDialogProps> = ({ open, promptSections, onClose, onInsert }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    // Dialog's focus trap runs during its enter transition, so focus after it has
    // mounted rather than relying on TextField's one-time `autoFocus` prop.
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: RADIUS_CARD,
            backgroundColor: GLASS_BACKGROUND,
            backdropFilter: GLASS_BLUR,
            boxShadow: SHADOW,
            border: `1px solid ${alpha(NAVY, 0.06)}`,
          },
        },
      }}
    >
      <DialogTitle sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY, pt: 3, pb: 1, px: 3 }}>
        Agregar sección al prompt
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        <Typography sx={{ fontFamily: 'Jost, sans-serif', color: alpha(NAVY, 0.62), fontSize: '0.875rem', mb: 2 }}>
          Busca una sección y selecciónala para insertarla donde está el cursor.
        </Typography>
        <Autocomplete
          autoHighlight
          options={promptSections}
          getOptionLabel={(option) => option.displayName || option.name}
          isOptionEqualToValue={(option, value) => option.name === value.name}
          onChange={(_, section) => {
            if (section) onInsert(`<SECTION:${section.name}>`);
          }}
          noOptionsText="No hay secciones disponibles"
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                borderRadius: RADIUS_CARD,
                backgroundColor: GLASS_BACKGROUND,
                backdropFilter: GLASS_BLUR,
                boxShadow: SHADOW,
                border: `1px solid ${alpha(NAVY, 0.06)}`,
              },
            },
          }}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.name} sx={{ py: 1.25, px: 1.5 }}>
              <Box>
                <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 600, color: NAVY }}>
                  {option.displayName || option.name}
                </Typography>
                {option.displayName && (
                  <Typography sx={{ fontFamily: 'Jost, sans-serif', color: alpha(NAVY, 0.58), fontSize: '0.8rem' }}>
                    {option.name}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={inputRef}
              placeholder="Escribe el nombre de una sección…"
              slotProps={{
                ...params.slotProps,
                htmlInput: { ...params.slotProps.htmlInput, 'aria-label': 'Buscar sección de prompt' },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: RADIUS_ITEM,
                  backgroundColor: SURFACE_TINT,
                  '& fieldset': { border: 'none' },
                  '&.Mui-focused': { boxShadow: `inset 0 0 0 2px ${alpha(NAVY, 0.22)}` },
                },
              }}
            />
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <BackButton onClick={onClose}>Cancelar</BackButton>
      </DialogActions>
    </Dialog>
  );
};

export default PromptSectionPickerDialog;
